import React, {
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { FiCopy, FiFlag, FiPlus, FiStar, FiThumbsDown, FiThumbsUp } from "react-icons/fi";
import useToast from "hooks/useToast";
import {
  clearChatHistory,
  fetchChatHistory,
  startNewChat,
  streamChatReply,
  submitChatFeedback,
  submitMessageDislike,
  submitMessageLike,
  submitMessageReport,
} from "./chatBotApi";

const MESSAGE_ROLES = {
  USER: "user",
  ASSISTANT: "assistant",
};

const EXAM_TYPES = {
  ACADEMIC: "academic",
  CORE: "core",
};

const EXAM_CONFIG = {
  [EXAM_TYPES.ACADEMIC]: {
    name: "PTE Academic",
    ctx: "for study & migration",
  },
  [EXAM_TYPES.CORE]: {
    name: "PTE Core",
    ctx: "for Canada (IRCC-approved)",
  },
};

const STARTER_QUESTIONS = [
  "What's the most important question type in the test?",
  "How do I score 90 in Speaking?",
  "Can I use templates in PTE?",
];

const NOVA_STAR_PATH =
  "M12 1.6c.55 5 2.85 7.3 7.85 7.85-5 .55-7.3 2.85-7.85 7.85-.55-5-2.85-7.3-7.85-7.85 5-.55 7.3-2.85 7.85-7.85Z";

const REPORT_MIN_LENGTH = 10;
const SUBSCRIPTION_ROUTE = "/admin/plans";
const DEFAULT_ERROR_MESSAGE =
  "Sorry, I encountered an error. Please try again.";
const CORE_NOVA_ACTIVE =
  String(import.meta.env.VITE_CORE_NOVA_ACTIVE).toLowerCase() === "true";

function createMessageId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function esc(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function inlineMarkdown(text) {
  return esc(text)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>");
}

function formatChatMessage(source) {
  const text = String(source).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  if (!text) return "";

  const lines = text.split("\n");
  let out = "";
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trim();
    if (line === "") {
      i += 1;
      continue;
    }

    if (/^#{1,6}\s/.test(line)) {
      const headingText = line.replace(/^#{1,6}\s+/, "");
      out += `<h3 class="nova-md-heading"><strong>${inlineMarkdown(headingText)}</strong></h3>`;
      i += 1;
      continue;
    }

    if (/^\d+\.\s/.test(line)) {
      out += "<ol>";
      while (i < lines.length) {
        const item = lines[i].trim();
        if (!item || !/^\d+\.\s/.test(item)) break;
        out += `<li>${inlineMarkdown(item.replace(/^\d+\.\s+/, ""))}</li>`;
        i += 1;
      }
      out += "</ol>";
      continue;
    }

    if (/^-\s/.test(line)) {
      out += "<ul>";
      while (i < lines.length) {
        const item = lines[i].trim();
        if (!item || !/^-\s/.test(item)) break;
        out += `<li>${inlineMarkdown(item.replace(/^-\s+/, ""))}</li>`;
        i += 1;
      }
      out += "</ul>";
      continue;
    }

    out += `<p>${inlineMarkdown(line)}</p>`;
    i += 1;
  }

  return out || `<p>${inlineMarkdown(text)}</p>`;
}

function stripInlineMarkdown(text) {
  return String(text)
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
}

function markdownToPlainText(source) {
  const text = String(source).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  if (!text) return "";

  const lines = text.split("\n");
  const out = [];

  for (const line of lines) {
    if (line.trim() === "") {
      out.push("");
      continue;
    }

    const trimmed = line.trim();

    if (/^#{1,6}\s/.test(trimmed)) {
      out.push(stripInlineMarkdown(trimmed.replace(/^#{1,6}\s+/, "")));
      continue;
    }

    if (/^>\s?/.test(trimmed)) {
      out.push(stripInlineMarkdown(trimmed.replace(/^>\s?/, "")));
      continue;
    }

    if (/^\d+\.\s/.test(trimmed)) {
      out.push(stripInlineMarkdown(trimmed.replace(/^\d+\.\s+/, "")));
      continue;
    }

    if (/^[-*+]\s/.test(trimmed)) {
      out.push(stripInlineMarkdown(trimmed.replace(/^[-*+]\s+/, "")));
      continue;
    }

    out.push(stripInlineMarkdown(trimmed));
  }

  return out.join("\n").replace(/<[^>]+>/g, "");
}

function formatChatMessageWithCaret(source) {
  return `${formatChatMessage(source)}<span class="nova-caret"></span>`;
}

async function copyToClipboard(text) {
  const value = String(text || "");

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return true;
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();

  try {
    const success = document.execCommand("copy");
    document.body.removeChild(textarea);
    return success;
  } catch {
    document.body.removeChild(textarea);
    return false;
  }
}

function NovaAvatar({ size = "md" }) {
  return (
    <div className={`nova-ava ${size === "lg" ? "lg" : ""}`} aria-hidden="true">
      <svg viewBox="0 0 24 24">
        <path d={NOVA_STAR_PATH} />
      </svg>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="nova-typing" aria-label="Nova is typing">
      <i />
      <i />
      <i />
    </div>
  );
}

function ReportPopover({ threadId, messageIndex, onClose }) {
  const { showToast } = useToast();
  const popoverRef = useRef(null);
  const textareaRef = useRef(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    const trimmed = reason.trim();
    if (trimmed.length < REPORT_MIN_LENGTH) {
      setError(`Please enter at least ${REPORT_MIN_LENGTH} characters.`);
      return;
    }

    if (!threadId) {
      showToast({
        type: 2,
        message: "Unable to report this message. Please try again later.",
      });
      return;
    }

    setError("");
    setIsSubmitting(true);

    try {
      await submitMessageReport({
        threadId,
        messageIndex,
        comment: trimmed,
      });
      showToast({ type: 1, message: "Report submitted. Thank you for your feedback." });
      onClose();
    } catch (submitError) {
      showToast({
        type: 2,
        message: submitError?.message || "Failed to submit report.",
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [messageIndex, onClose, reason, showToast, threadId]);

  return (
    <div className="nova-report-popover" ref={popoverRef} role="dialog" aria-label="Report message">
      <p className="nova-report-title">Report this response</p>
      <textarea
        ref={textareaRef}
        rows={3}
        value={reason}
        placeholder="Describe the issue with this response..."
        onChange={(event) => {
          setReason(event.target.value);
          if (error) setError("");
        }}
        disabled={isSubmitting}
      />
      {error && <p className="nova-report-error">{error}</p>}
      <div className="nova-report-actions">
        <button type="button" className="cancel" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </button>
        <button type="button" className="submit" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? "Submitting..." : "Submit report"}
        </button>
      </div>
    </div>
  );
}

function MessageActions({ message, threadId, messageIndex, onFeedback }) {
  const { showToast } = useToast();
  const [reportOpen, setReportOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleLike = useCallback(async () => {
    const liked = !message.feedback?.liked;
    onFeedback({ liked, disliked: false });
    try {
      await submitMessageLike(message.id, liked);
    } catch {
      showToast({ type: 2, message: "Failed to submit feedback." });
    }
  }, [message.feedback?.liked, message.id, onFeedback, showToast]);

  const handleDislike = useCallback(async () => {
    const disliked = !message.feedback?.disliked;
    onFeedback({ liked: false, disliked });
    try {
      await submitMessageDislike(message.id, disliked);
    } catch {
      showToast({ type: 2, message: "Failed to submit feedback." });
    }
  }, [message.feedback?.disliked, message.id, onFeedback, showToast]);

  const handleCopy = useCallback(async () => {
    const success = await copyToClipboard(markdownToPlainText(message.content));
    if (success) {
      setCopied(true);
      // showToast({ type: 1, message: "Response copied to clipboard." });
      setTimeout(() => setCopied(false), 2000);
    } else {
      // showToast({ type: 2, message: "Failed to copy response." });
    }
  }, [message.content, showToast]);

  return (
    <div className="nova-acts">
      {/* <button
        type="button"
        className={`up ${message.feedback?.liked ? "act-on" : ""}`}
        title="Helpful"
        aria-label="Mark response as helpful"
        onClick={handleLike}
      >
        <FiThumbsUp size={14} />
      </button>
      <button
        type="button"
        className={`down ${message.feedback?.disliked ? "act-on" : ""}`}
        title="Not helpful"
        aria-label="Mark response as not helpful"
        onClick={handleDislike}
      >
        <FiThumbsDown size={14} />
      </button> */}
      <div className="nova-report-wrap">
        <button
          type="button"
          className={`report ${reportOpen ? "act-on" : ""}`}
          title="Report"
          aria-label="Report response"
          aria-expanded={reportOpen}
          onClick={() => setReportOpen((open) => !open)}
        >
          <FiFlag size={14} />
        </button>
        {reportOpen && (
          <ReportPopover
            threadId={threadId}
            messageIndex={messageIndex}
            onClose={() => setReportOpen(false)}
          />
        )}
      </div>
      {copied ? (
        <span className="ok">Copied</span>
      ) : (
        <button
          type="button"
          className="copy"
          title="Copy"
          aria-label="Copy response"
          onClick={handleCopy}
        >
          <FiCopy size={14} />
        </button>
      )}
    </div>
  );
}

const ChatMessage = memo(function ChatMessage({
  message,
  threadId,
  messageIndex,
  onFeedback,
  onRetry,
}) {
  const isUser = message.role === MESSAGE_ROLES.USER;
  const showActions =
    !isUser && !message.isStreaming && !message.error && message.content;
  const showTyping = !isUser && message.isStreaming && !message.content;
  const showRetry = !isUser && message.error;

  let bubbleContent;
  if (isUser) {
    bubbleContent = message.content;
  } else if (showTyping) {
    bubbleContent = <TypingIndicator />;
  } else if (message.isStreaming) {
    bubbleContent = (
      <span
        dangerouslySetInnerHTML={{
          __html: formatChatMessageWithCaret(message.content),
        }}
      />
    );
  } else {
    bubbleContent = (
      <span
        dangerouslySetInnerHTML={{
          __html: formatChatMessage(message.content),
        }}
      />
    );
  }

  return (
    <div className={`nova-row ${isUser ? "me" : "nova"}`}>
      {!isUser && <NovaAvatar />}
      <div className="nova-msg-wrap">
        <div className="nova-bubble">{bubbleContent}</div>
        {showActions && (
          <MessageActions
            message={message}
            threadId={threadId}
            messageIndex={messageIndex}
            onFeedback={onFeedback}
          />
        )}
        {showRetry && (
          <div className="nova-retry-wrap">
            <button type="button" className="nova-retry-btn" onClick={onRetry}>
              Retry
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

function useChatBotState(subscribed) {
  const { showToast } = useToast();
  const abortRef = useRef(null);
  const rafRef = useRef(null);
  const pendingContentRef = useRef("");

  const [messages, setMessages] = useState([]);
  const [examType, setExamType] = useState(EXAM_TYPES.ACADEMIC);
  const [threadId, setThreadId] = useState(null);
  const [isBusy, setIsBusy] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [pendingExamType, setPendingExamType] = useState(null);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [lastFailedQuestion, setLastFailedQuestion] = useState(null);

  const hasMessages = messages.length > 0;

  useEffect(() => {
    let cancelled = false;

    const loadHistory = async () => {
      setIsLoadingHistory(true);
      try {
        const result = await fetchChatHistory();
        if (cancelled) return;

        const historyMessages = Array.isArray(result?.messages)
          ? result.messages
          : [];
        const mapped = historyMessages
          .filter(
            (message) =>
              message &&
              (message.role === MESSAGE_ROLES.USER ||
                message.role === MESSAGE_ROLES.ASSISTANT) &&
              typeof message.content === "string"
          )
          .map((message) => ({
            id: createMessageId(),
            role: message.role,
            content: message.content,
            timestamp: Date.now(),
          }));

        if (mapped.length) {
          setMessages(mapped);
          setThreadId(result.thread_id || null);
          if (result.exam_type === EXAM_TYPES.ACADEMIC) {
            setExamType(EXAM_TYPES.ACADEMIC);
          } else if (result.exam_type === EXAM_TYPES.CORE && CORE_NOVA_ACTIVE) {
            setExamType(EXAM_TYPES.CORE);
          }
        }
      } catch {
        // Soft-fail: blank chat, no toast
      } finally {
        if (!cancelled) {
          setIsLoadingHistory(false);
        }
      }
    };

    loadHistory();

    return () => {
      cancelled = true;
    };
  }, []);

  const cancelStream = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    pendingContentRef.current = "";
  }, []);

  const updateMessage = useCallback((messageId, patch) => {
    setMessages((prev) =>
      prev.map((message) =>
        message.id === messageId ? { ...message, ...patch } : message
      )
    );
  }, []);

  const updateMessageFeedback = useCallback((messageId, feedbackPatch) => {
    setMessages((prev) =>
      prev.map((message) =>
        message.id === messageId
          ? {
              ...message,
              feedback: { ...message.feedback, ...feedbackPatch },
            }
          : message
      )
    );
  }, []);

  const streamReply = useCallback(
    async ({ question, examType: currentExamType, onTokenUpdate, onComplete, onError }) => {
      cancelStream();

      const controller = new AbortController();
      abortRef.current = controller;
      pendingContentRef.current = "";

      const flushPending = () => {
        rafRef.current = null;
        onTokenUpdate(pendingContentRef.current);
      };

      const scheduleFlush = () => {
        if (rafRef.current) return;
        rafRef.current = requestAnimationFrame(flushPending);
      };

      const appendToken = (token) => {
        if (!token) return;
        pendingContentRef.current = `${pendingContentRef.current}${token}`;
        scheduleFlush();
      };

      try {
        await streamChatReply({
          question,
          examType: currentExamType,
          signal: controller.signal,
          onToken: appendToken,
          onError: appendToken,
          onDone: () => {
            if (rafRef.current) {
              cancelAnimationFrame(rafRef.current);
              rafRef.current = null;
            }
            onTokenUpdate(pendingContentRef.current);
          },
        });

        if (controller.signal.aborted) return;

        onComplete({
          content: pendingContentRef.current,
          aborted: false,
        });
      } catch (error) {
        if (error?.name === "AbortError") {
          onComplete({
            content: pendingContentRef.current,
            aborted: true,
          });
          return;
        }

        onError(error);
      } finally {
        abortRef.current = null;
        pendingContentRef.current = "";
      }
    },
    [cancelStream]
  );

  const runStream = useCallback(
    async (question, assistantMessageId) => {
      setIsBusy(true);
      setIsStreaming(true);
      setLastFailedQuestion(null);

      await streamReply({
        question,
        examType,
        onTokenUpdate: (content) => {
          updateMessage(assistantMessageId, { content });
        },
        onComplete: ({ content, aborted }) => {
          setIsStreaming(false);
          setIsBusy(false);

          if (aborted) {
            updateMessage(assistantMessageId, {
              content: content || "",
              isStreaming: false,
            });
            return;
          }

          updateMessage(assistantMessageId, {
            content: content || "I couldn't generate a response. Please try again.",
            isStreaming: false,
            error: !content,
          });
        },
        onError: (error) => {
          setIsStreaming(false);
          setIsBusy(false);
          setLastFailedQuestion(question);

          const errorMessage = error?.message || DEFAULT_ERROR_MESSAGE;

          updateMessage(assistantMessageId, {
            content: errorMessage,
            isStreaming: false,
            error: true,
          });

          showToast({ type: 2, message: errorMessage });
        },
      });
    },
    [examType, showToast, streamReply, updateMessage]
  );

  const sendMessage = useCallback(
    async (text) => {
      if (!subscribed) return;

      const question = String(text || "").trim();
      if (!question || isBusy || isStreaming) return;

      const userMessage = {
        id: createMessageId(),
        role: MESSAGE_ROLES.USER,
        content: question,
        timestamp: Date.now(),
      };

      const assistantMessage = {
        id: createMessageId(),
        role: MESSAGE_ROLES.ASSISTANT,
        content: "",
        timestamp: Date.now(),
        isStreaming: true,
      };

      setMessages((prev) => [...prev, userMessage, assistantMessage]);
      await runStream(question, assistantMessage.id);
    },
    [isBusy, isStreaming, runStream, subscribed]
  );

  const retryLastMessage = useCallback(async () => {
    if (!lastFailedQuestion || isBusy || isStreaming) return;

    const assistantMessage = {
      id: createMessageId(),
      role: MESSAGE_ROLES.ASSISTANT,
      content: "",
      timestamp: Date.now(),
      isStreaming: true,
    };

    setMessages((prev) => {
      const withoutErrors = prev.filter((message) => !message.error);
      return [...withoutErrors, assistantMessage];
    });

    await runStream(lastFailedQuestion, assistantMessage.id);
  }, [isBusy, isStreaming, lastFailedQuestion, runStream]);

  const resetConversation = useCallback(() => {
    cancelStream();
    setMessages([]);
    setThreadId(null);
    setIsBusy(false);
    setIsStreaming(false);
    setLastFailedQuestion(null);
  }, [cancelStream]);

  const confirmNewChat = useCallback(
    async ({ rating = null, comment = "" } = {}) => {
      if (isClearing) return;

      cancelStream();
      setIsClearing(true);

      try {
        const trimmedComment = String(comment || "").trim();
        const hasFeedback = rating != null || trimmedComment.length > 0;

        if (hasFeedback) {
          if (!threadId) {
            throw new Error("Unable to submit feedback. Please try again later.");
          }
          await submitChatFeedback({
            threadId,
            rating: rating == null ? null : rating,
            comment: trimmedComment,
          });
        }

        const result = await startNewChat(examType);
        showToast({ type: 1, message: "New chat started." });
        setShowNewChatModal(false);
        resetConversation();
        if (result?.thread_id) {
          setThreadId(result.thread_id);
        }
      } catch (error) {
        showToast({
          type: 2,
          message: error?.message || "Failed to start a new chat.",
        });
      } finally {
        setIsClearing(false);
      }
    },
    [cancelStream, examType, isClearing, resetConversation, showToast, threadId]
  );

  const requestNewChat = useCallback(() => {
    if (isClearing || isLoadingHistory) return;

    if (hasMessages) {
      setShowNewChatModal(true);
      return;
    }

    confirmNewChat();
  }, [confirmNewChat, hasMessages, isClearing, isLoadingHistory]);

  const cancelNewChat = useCallback(() => {
    setShowNewChatModal(false);
  }, []);

  const applyExamSwitch = useCallback(
    async (nextExamType, { rating = null, comment = "" } = {}) => {
      if (nextExamType === examType) return;

      cancelStream();
      setIsClearing(true);

      try {
        const trimmedComment = String(comment || "").trim();
        const hasFeedback = rating != null || trimmedComment.length > 0;

        if (hasFeedback) {
          if (!threadId) {
            throw new Error("Unable to submit feedback. Please try again later.");
          }
          await submitChatFeedback({
            threadId,
            rating: rating == null ? null : rating,
            comment: trimmedComment,
          });
        }

        try {
          await clearChatHistory(examType);
        } catch (clearError) {
          showToast({
            type: 2,
            message: clearError?.message || "Failed to clear previous chat history.",
          });
        }

        setExamType(nextExamType);
        resetConversation();
        setPendingExamType(null);

        const result = await startNewChat(nextExamType);
        if (result?.thread_id) {
          setThreadId(result.thread_id);
        }
        showToast({ type: 1, message: `Switched to ${EXAM_CONFIG[nextExamType]?.name || nextExamType}.` });
      } catch (error) {
        showToast({
          type: 2,
          message: error?.message || "Failed to switch exam type.",
        });
      } finally {
        setIsClearing(false);
      }
    },
    [cancelStream, examType, resetConversation, showToast, threadId]
  );

  const requestExamSwitch = useCallback(
    (nextExamType) => {
      if (nextExamType === examType) return;
      if (nextExamType === EXAM_TYPES.CORE && !CORE_NOVA_ACTIVE) return;
      if (!hasMessages) {
        setExamType(nextExamType);
        return;
      }
      setPendingExamType(nextExamType);
    },
    [examType, hasMessages]
  );

  const cancelExamSwitch = useCallback(() => {
    setPendingExamType(null);
  }, []);

  return {
    messages,
    examType,
    threadId,
    isBusy,
    isStreaming,
    isClearing,
    isLoadingHistory,
    pendingExamType,
    showNewChatModal,
    sendMessage,
    retryLastMessage,
    requestNewChat,
    confirmNewChat,
    cancelNewChat,
    requestExamSwitch,
    applyExamSwitch,
    cancelExamSwitch,
    updateMessageFeedback,
    cancelStream,
  };
}

const ChatBotPanel = forwardRef(function ChatBotPanel(
  { subscribed, onClose },
  ref
) {
  const navigate = useNavigate();
  const threadRef = useRef(null);
  const textareaRef = useRef(null);
  const newChatTextareaRef = useRef(null);

  const {
    messages,
    examType,
    threadId,
    isBusy,
    isStreaming,
    isClearing,
    isLoadingHistory,
    pendingExamType,
    showNewChatModal,
    sendMessage,
    retryLastMessage,
    requestNewChat,
    confirmNewChat,
    cancelNewChat,
    requestExamSwitch,
    applyExamSwitch,
    cancelExamSwitch,
    updateMessageFeedback,
    cancelStream,
  } = useChatBotState(subscribed);

  const [feedbackComment, setFeedbackComment] = useState("");
  const [feedbackRating, setFeedbackRating] = useState(null);
  const [feedbackHoverRating, setFeedbackHoverRating] = useState(null);
  const [feedbackError, setFeedbackError] = useState("");
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  useImperativeHandle(ref, () => ({ cancelStream }), [cancelStream]);

  const composerDisabled =
    isBusy || isStreaming || isClearing || isLoadingHistory;
  const exam = EXAM_CONFIG[examType];
  const streamingContent =
    messages.find((message) => message.isStreaming)?.content || "";

  useEffect(() => {
    const thread = threadRef.current;
    if (!thread) return;
    thread.scrollTo({ top: thread.scrollHeight, behavior: "smooth" });
  }, [messages.length, streamingContent]);

  useEffect(() => {
    if (subscribed) {
      textareaRef.current?.focus();
    }
  }, [subscribed]);

  const showFeedbackModal = showNewChatModal || Boolean(pendingExamType);
  const feedbackMode = showNewChatModal ? "newChat" : pendingExamType ? "examSwitch" : null;

  useEffect(() => {
    if (showFeedbackModal) {
      setFeedbackComment("");
      setFeedbackRating(null);
      setFeedbackHoverRating(null);
      setFeedbackError("");
      setIsSubmittingFeedback(false);
      newChatTextareaRef.current?.focus();
    }
  }, [showFeedbackModal]);

  const autosize = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  }, []);

  const handleSend = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const value = textarea.value.trim();
    if (!value || composerDisabled) return;
    sendMessage(value);
    textarea.value = "";
    textarea.style.height = "auto";
  }, [composerDisabled, sendMessage]);

  const handleComposerKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleFeedbackCancel = useCallback(() => {
    if (isClearing || isSubmittingFeedback) return;
    if (feedbackMode === "newChat") {
      cancelNewChat();
    } else {
      cancelExamSwitch();
    }
  }, [
    cancelExamSwitch,
    cancelNewChat,
    feedbackMode,
    isClearing,
    isSubmittingFeedback,
  ]);

  const handleFeedbackSubmit = useCallback(async () => {
    if (isClearing || isSubmittingFeedback) return;

    const payload = {
      rating: feedbackRating,
      comment: feedbackComment.trim(),
    };

    setFeedbackError("");
    setIsSubmittingFeedback(true);

    try {
      if (feedbackMode === "newChat") {
        await confirmNewChat(payload);
      } else if (feedbackMode === "examSwitch" && pendingExamType) {
        await applyExamSwitch(pendingExamType, payload);
      }
    } finally {
      setIsSubmittingFeedback(false);
    }
  }, [
    applyExamSwitch,
    confirmNewChat,
    feedbackComment,
    feedbackMode,
    feedbackRating,
    isClearing,
    isSubmittingFeedback,
    pendingExamType,
  ]);

  const pendingExamName =
    EXAM_CONFIG[pendingExamType]?.name || pendingExamType;
  const feedbackBusy = isClearing || isSubmittingFeedback;
  const feedbackTitle =
    feedbackMode === "examSwitch"
      ? `Switch to ${pendingExamName}?`
      : "Start a new chat?";
  const feedbackPrimaryLabel =
    feedbackMode === "examSwitch"
      ? feedbackBusy
        ? "Switching..."
        : "Switch & start fresh"
      : feedbackBusy
        ? "Starting..."
        : "Start new chat";
  const feedbackDescription =
    feedbackMode === "examSwitch"
      ? "Share quick feedback about this conversation before switching. This starts a fresh chat."
      : "Share quick feedback about this conversation before starting fresh.";

  return (
    <div className="nova-panel" role="dialog" aria-label="Nova AI chat">
      <div className="nova-chat">
        <div className="nova-chat-head">
          <div className="nova-brand">
            <NovaAvatar />
            <div className="nm">
              <b>
                Nova <span className="nova-dot" title="Online" />
              </b>
              <small>Your AI PTE Coach</small>
            </div>
          </div>
          <div className="nova-head-right">
            <button
              type="button"
              className="nova-cred"
              title="New chat"
              aria-label="Start new chat"
              disabled={isClearing || isLoadingHistory}
              onClick={requestNewChat}
            >
              <FiPlus size={14} />
              {isClearing ? "Starting..." : "New chat"}
            </button>
            <button
              type="button"
              className="nova-close"
              aria-label="Close chat"
              onClick={onClose}
            >
              ×
            </button>
          </div>
        </div>

        <div className="nova-subbar">
          <div className="nova-seg" role="tablist" aria-label="Exam type">
            <button
              type="button"
              role="tab"
              aria-selected={examType === EXAM_TYPES.ACADEMIC}
              className={examType === EXAM_TYPES.ACADEMIC ? "on" : ""}
              disabled={composerDisabled}
              onClick={() => requestExamSwitch(EXAM_TYPES.ACADEMIC)}
            >
              Academic
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={examType === EXAM_TYPES.CORE}
              className={examType === EXAM_TYPES.CORE ? "on" : ""}
              disabled={composerDisabled || !CORE_NOVA_ACTIVE}
              title={
                CORE_NOVA_ACTIVE ? undefined : "PTE Core is not available yet"
              }
              aria-disabled={!CORE_NOVA_ACTIVE}
              onClick={() => requestExamSwitch(EXAM_TYPES.CORE)}
            >
              Core
            </button>
          </div>
          <div className="nova-ctx">
            <b>{exam.name}</b> · {exam.ctx}
          </div>
        </div>

        <div
          className="nova-thread"
          ref={threadRef}
          role="log"
          aria-live="polite"
          aria-relevant="additions text"
        >
          {isLoadingHistory && !messages.length && (
            <div className="nova-history-loading" role="status" aria-live="polite">
              <div className="nova-history-loading-ava">
                <NovaAvatar size="lg" />
              </div>
              <h2>Loading your chat…</h2>
              <p>Nova is restoring your previous conversation.</p>
              <div className="nova-history-loading-bar" aria-hidden="true">
                <span />
              </div>
            </div>
          )}
          {!isLoadingHistory && !messages.length && (
            <div className="nova-hero">
              <NovaAvatar size="lg" />
              <h1>Hi, I'm Nova 👋</h1>
              <p className="sub d-none">
                Your AI PTE coach — built by <b>Varun Dhawan</b>, a 14× PTE Perfect 90
                scorer with 8+ years' experience. Ask me anything about your{" "}
                <b>{exam.name}</b> exam. Let's get you to <b>90</b>.
              </p>
              <p className="sub">
                Your AI PTE coach powered by <b>RAG (Retrieval-Augmented Generation)</b>.
                Ask me anything about your <b>PTE exam</b>.
                {/* using the knowledge from uploaded documents. */}
              </p>
              <div className="trust d-none">
                <span className="gold">★ 14× PTE Perfect 90</span>
                <span>8+ years</span>
                <span>4.8★ app</span>
                <span>50,000+ successful students</span>
              </div>
              <div className="starters">
                {STARTER_QUESTIONS.map((question, index) => (
                  <button
                    key={question}
                    type="button"
                    className="starter"
                    disabled={composerDisabled || !subscribed}
                    onClick={() => sendMessage(question)}
                  >
                    <span className="q">{index + 1}</span>
                    <span>{question}</span>
                    <span className="arr">→</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((message, index) => (
            <ChatMessage
              key={message.id}
              message={message}
              threadId={threadId}
              messageIndex={index}
              onFeedback={(feedback) =>
                updateMessageFeedback(message.id, feedback)
              }
              onRetry={retryLastMessage}
            />
          ))}
        </div>

        {!subscribed ? (
          <div className="nova-composer nova-composer-subscribe">
            <button
              type="button"
              className="nova-subscribe-btn"
              onClick={() => navigate(SUBSCRIPTION_ROUTE)}
            >
              Subscribe Now
            </button>
            <div className="nova-disc">
              Nova is a Premium feature. Subscribe to unlock your 24/7 AI PTE coach.
            </div>
          </div>
        ) : (
          <div className="nova-composer">
            <div className="nova-inputwrap">
              <textarea
                ref={textareaRef}
                rows={1}
                placeholder="Ask Nova about your PTE exam…"
                aria-label="Message Nova"
                disabled={composerDisabled}
                onInput={autosize}
                onKeyDown={handleComposerKeyDown}
              />
              <button
                type="button"
                className="nova-send"
                title="Send"
                aria-label="Send message"
                disabled={composerDisabled}
                onClick={handleSend}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M3.4 20.4 21 12 3.4 3.6 3.4 10l11 2-11 2z" />
                </svg>
              </button>
            </div>
            <div className="nova-disc">
              Nova is an AI coach and can occasionally be wrong — always confirm
              exam-day rules with your LA coach.
            </div>
          </div>
        )}

        {showFeedbackModal && (
          <div
            className="nova-modal show"
            role="dialog"
            aria-modal="true"
            aria-labelledby="nova-feedback-title"
          >
            <div className="nova-mcard nova-new-chat-card">
              <h3 id="nova-feedback-title">{feedbackTitle}</h3>
              <p>{feedbackDescription}</p>

              <div
                className="nova-star-rating"
                role="radiogroup"
                aria-label="Conversation rating"
              >
                {[1, 2, 3, 4, 5].map((star) => {
                  const activeValue = feedbackHoverRating ?? feedbackRating ?? 0;
                  const isFilled = star <= activeValue;
                  return (
                    <button
                      key={star}
                      type="button"
                      className={`nova-star ${isFilled ? "filled" : ""}`}
                      role="radio"
                      aria-checked={feedbackRating === star}
                      aria-label={`${star} star${star === 1 ? "" : "s"}`}
                      disabled={feedbackBusy}
                      onMouseEnter={() => setFeedbackHoverRating(star)}
                      onMouseLeave={() => setFeedbackHoverRating(null)}
                      onClick={() =>
                        setFeedbackRating((current) =>
                          current === star ? null : star
                        )
                      }
                    >
                      <FiStar size={22} aria-hidden="true" />
                    </button>
                  );
                })}
              </div>

              <textarea
                ref={newChatTextareaRef}
                rows={4}
                value={feedbackComment}
                placeholder="How was your chat with Nova? What could be improved?"
                aria-label="Conversation feedback"
                style={{ resize: "none" }}
                disabled={feedbackBusy}
                onChange={(event) => {
                  setFeedbackComment(event.target.value);
                  if (feedbackError) setFeedbackError("");
                }}
              />
              {feedbackError && (
                <p className="nova-new-chat-error">{feedbackError}</p>
              )}
              <div className="nova-mact">
                <button
                  type="button"
                  className="cancel"
                  onClick={handleFeedbackCancel}
                  disabled={feedbackBusy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="go"
                  onClick={handleFeedbackSubmit}
                  disabled={feedbackBusy}
                >
                  {feedbackPrimaryLabel}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <span className="nova-panel-context" aria-hidden="true">
        {exam.name}
      </span>
    </div>
  );
});

export default ChatBotPanel;
